import React, { useState, useEffect } from "react";
import {
  FormHelperText,
  TextField,
  Box,
  Button,
  Grid,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import * as Yup from "yup";
import { Formik } from "formik";
import useMounted from "../../../hooks/useMounted";
import toast from "react-hot-toast";
import { updateIssue } from "../../../api/project/Project";
import { makeStyles } from "@mui/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import issueStatus from "../../../@const/issueStatus";
import useAuth from "../../../hooks/useAuth";
import moment from "moment";
import SelectUserDropdown from "../../SelectUserDropdown";
import FileUploader from "../../FileUploader";

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: 120,
    width: "100%",
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
}));
const EditIssueModal = ({
  issue,
  showEditIssueModal,
  setShowEditIssueModal,
  getIssues,
}) => {
  const mounted = useMounted();
  const [selectedFile, setSelectedFile] = useState(null);
  const [taskStartDate, setTaskStartDate] = useState(
    new Date(issue.start_date)
  );
  const [taskEndDate, setTaskEndDate] = useState(new Date(issue.end_date));
  const [issueStartDate, setIssueStartDate] = useState(
    moment(issue.start_date)
  );
  const [issueEndDate, setIssueEndDate] = useState(moment(issue.end_date));

  const { user } = useAuth();
  const classes = useStyles();

  return (
    <>
      <Dialog
        open={showEditIssueModal}
        onClose={() => setShowEditIssueModal(false)}
        maxWidth={"md"}
      >
        <Formik
          initialValues={{
            name: issue.name,
            status: issue.get_status.id,
            notes: issue.description,
            assigned_to: issue.assigned_to,
            submit: null,
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().max(255).required("Issue Name is required"),
            status: Yup.string().max(255).required("Issue status is required"),
            notes: Yup.string()
              .max(255)
              .required("Milestone notes is required"),
          })}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
              let data = new FormData();
              data.append("task_doc", selectedFile);
              data.append("issue_name", values.name);
              data.append("issue_status", values.status);
              data.append(
                "issue_start",
                moment(taskStartDate).format("YYYY/MM/DD/")
              );
              data.append(
                "issue_end",
                moment(taskEndDate).format("YYYY/MM/DD/")
              );
              data.append("issue_assignee", values?.assigned_to || "");
              data.append("task_id", "");
              data.append("issue_desc", values.notes || "");
              data.append("issue_id", issue.id);
              const res = await updateIssue(data, issue.id);

              if (res.data.success) {
                toast.success("New issue added");
                getIssues();
                setShowEditIssueModal(false);
              }
              if (mounted.current) {
                setStatus({ success: true });
                setSubmitting(false);
              }
            } catch (err) {
              console.error(err);
              toast.error("Something went wrong");
              setStatus({ success: false });
              setErrors({ submit: err.message });
              setSubmitting(false);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogTitle>Edit Issue</DialogTitle>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Issue Name"
                      required
                      error={Boolean(touched.name && errors.name)}
                      fullWidth
                      helperText={touched.name && errors.name}
                      margin="normal"
                      name="name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.name}
                      variant="outlined"
                      sx={{ mt: 0 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ width: "100%" }}>
                      {issueStatus.length > 0 && (
                        <FormControl className={classes.formControl}>
                          <InputLabel id="select-status" sx={{ color: "#bbb" }}>
                            Status
                          </InputLabel>
                          <Select
                            labelId="select-status"
                            label="Status"
                            name="status"
                            value={values.status}
                            error={Boolean(touched.status && errors.status)}
                            fullWidth
                            helperText={touched.status && errors.type}
                            required
                            placeholder="Status"
                            onBlur={handleBlur}
                            onChange={handleChange}
                          >
                            <MenuItem vaalue="">none</MenuItem>
                            {issueStatus.map((status) => (
                              <MenuItem key={status.id} value={status.id}>
                                {status.type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <SelectUserDropdown
                      setSelectedUser={(user_id) => {
                        values.assigned_to = user_id.id;
                      }}
                      defaultUser={issue.get_issue_assigned_to}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={6}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <FormControl
                        fullWidth
                        className={classes.formControl}
                        required
                        error={!issueStartDate}
                      >
                        <DatePicker
                          label="Issue Start Date"
                          value={issueStartDate}
                          onChange={(newValue) => {
                            if (newValue && moment.isMoment(newValue)) {
                              setIssueStartDate(newValue);
                              setTaskStartDate(newValue.toDate());
                              setTaskEndDate(newValue.toDate());
                            }
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: "outlined",
                              className: "pt-datepicker-ip",
                            },
                          }}
                        />
                      </FormControl>
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <FormControl
                        fullWidth
                        className={classes.formControl}
                        required
                        error={!issueEndDate}
                      >
                        <DatePicker
                          label="Issue End Date"
                          value={issueEndDate}
                          onChange={(newValue) => {
                            if (newValue && moment.isMoment(newValue)) {
                              setIssueEndDate(newValue);
                              setTaskEndDate(newValue.toDate());
                            }
                          }}
                          minDate={issueStartDate}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: "outlined",
                              className: "pt-datepicker-ip",
                            },
                          }}
                        />
                      </FormControl>
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Description"
                      required
                      error={Boolean(touched.notes && errors.notes)}
                      fullWidth
                      helperText={touched.notes && errors.notes}
                      margin="normal"
                      name="notes"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.notes}
                      variant="outlined"
                      sx={{ mt: 0 }}
                      multiline
                      minRows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FileUploader
                      onFileSelectSuccess={(file) => setSelectedFile(file)}
                      onFileSelectError={({ error }) => alert(error)}
                    />
                  </Grid>
                </Grid>

                {errors.submit && (
                  <Box sx={{ mt: 3 }}>
                    <FormHelperText error>{errors.submit}</FormHelperText>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Box sx={{ display: "flex", mr: "auto", px: 2, pb: 1 }}>
                  <Box>
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      Save
                    </Button>
                  </Box>
                  <Box sx={{ ml: 1 }}>
                    <Button
                      disabled={isSubmitting}
                      size="large"
                      variant="contained"
                      onClick={() => setShowEditIssueModal(false)}
                      sx={{ backgroundColor: "#C0CBDE" }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default EditIssueModal;
